import { ArrowRight, Boxes, Download, Lock, Sparkles, Workflow } from "lucide-react";
import { Footer } from "@/components/Footer";
import { SmartLink } from "@/components/Header";
import { Reveal } from "@/components/site/Reveal";
import { PageMeta } from "@/components/site/blocks";
import { Marquee } from "@/components/site/showcase";
import { ArtBars, ArtNodes, ArtShield, ArtType, ResourceRail, type ResourceCard } from "@/components/site/parent";
import { ScrollStory } from "@/components/site/story";
import { AppDock, HeroBoard, WorkAreas } from "@/components/site/atl";
import { liveProducts, productPath } from "@/data/products";
import { GET_STARTED_URL } from "@/data/site";

// The homepage is about the company. Products are listed from data/products.ts and get
// their own pages; nothing on this page should need rewriting when the next one ships.
const beliefs = [
  { icon: Sparkles, title: "AI should finish the job", body: "We build agents that carry work through to a result, not assistants that hand the work back with suggestions." },
  { icon: Workflow, title: "Meet people where they work", body: "Our products connect to the tools a team already uses. Nothing to migrate, no new habit to force on anyone." },
  { icon: Lock, title: "People stay in charge", body: "You decide what runs on its own and what waits for you. Access uses official sign-ins and can be revoked in one click." },
  { icon: Boxes, title: "One account, every product", body: "ClearMakes Inbox, ClearMakes Agents and ClearMeet open with the same login, so each new product is a click away, not a new vendor." },
];

const facts = ["AI products for real work", "Connect the apps you use", "Credentials encrypted at rest", "Revoke access in one click", "You approve what AI does", "One login for every product", "Free to start"];

const HERO_PRODUCTS = [
  {
    name: "ClearMakes Inbox",
    line: "Workflows and answers for your apps",
    href: "/products/inbox",
    logo: "/brand/inbox-mark-256.png",
  },
  {
    name: "ClearMakes Agents",
    line: "AI agents for team and website",
    href: "https://agents.clearmakes.com",
    logo: "/brand/agents-mark-256.png",
  },
  {
    name: "ClearMeet",
    line: "Live answers in every meeting",
    href: "https://meet.clearmakes.com",
    logo: "/brand/meet-mark-256.png",
  },
];

const resources: ResourceCard[] = [
  { tag: "Company", title: "Why we started ClearMakes, and what we are building toward", cta: "About us", href: "/company", color: "hsl(140 45% 26%)", art: <ArtNodes color="hsl(95 55% 55%)" /> },
  { tag: "Products", title: "Every ClearMakes product, and what each one is for", cta: "See products", href: "/products", color: "hsl(22 90% 40%)", art: <ArtType top="Clear" bottom="Makes" /> },
  { tag: "Security", title: "How we handle access, tokens and revocation across products", cta: "Read now", href: "/security-architecture", color: "hsl(216 80% 40%)", art: <ArtShield bg="hsl(216 90% 95%)" color="hsl(216 84% 52%)" /> },
  { tag: "Library", title: "Guides, comparisons and playbooks in one place", cta: "Browse resources", href: "/resources", color: "hsl(0 0% 14%)", art: <ArtBars bg="hsl(40 9% 94%)" color="hsl(0 0% 12%)" /> },
];

const schema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://clearmakes.com/#organization",
      name: "ClearMakes",
      url: "https://clearmakes.com/",
      logo: { "@type": "ImageObject", url: "https://clearmakes.com/logo.png" },
      description: "ClearMakes makes intelligent software for the way teams actually work: people decide, AI agents do the rest. Less coordination, more finished work.",
      email: "contact@clearmakes.com",
      owns: liveProducts.map((p) => ({ "@id": `https://clearmakes.com/products/${p.slug}#product` })),
    },
    { "@type": "WebSite", "@id": "https://clearmakes.com/#website", url: "https://clearmakes.com/", name: "ClearMakes", publisher: { "@id": "https://clearmakes.com/#organization" } },
    {
      "@type": "ItemList",
      name: "ClearMakes products",
      itemListElement: liveProducts.map((p, i) => ({ "@type": "ListItem", position: i + 1, name: p.name, url: p.url ?? `https://clearmakes.com/products/${p.slug}` })),
    },
  ],
};

const Index = () => (
  <>
    <PageMeta
      title="ClearMakes — Less coordination. More finished work."
      description="ClearMakes makes intelligent software for the way teams actually work: people decide, AI agents do the rest. Less coordination, more finished work."
      path="/"
    />
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

    <main className="overflow-x-clip bg-background text-foreground">
      {/* Hero: the dock rides the bottom of the screen until this section ends */}
      <section aria-labelledby="hero-h" className="relative pb-10 pt-16 sm:pt-24">
        <div aria-hidden className="cm-grid-bg pointer-events-none absolute left-0 top-0 h-[560px] w-[min(900px,70vw)]" />
        <div className="cm-container relative text-center">
          <Reveal>
            <h1 id="hero-h" className="mx-auto max-w-5xl text-[2.9rem] font-bold leading-[1.04] tracking-[-0.04em] sm:text-[4.6rem] lg:text-[5.4rem]">
              Clear work for teams <span className="text-[hsl(285_70%_62%)]">and</span> their agents
            </h1>
            <p className="mx-auto mt-7 max-w-3xl text-[1.2rem] leading-snug text-foreground/85 sm:text-[1.5rem]">
              Where people decide and AI agents do the rest. Intelligent software for the way teams actually work.
            </p>
            {/* One door per product, equal weight: the only colour is each
                product's own mark, so none reads as the main one. */}
            <div className="mx-auto mt-10 grid max-w-2xl gap-3 sm:grid-cols-2 lg:max-w-5xl lg:grid-cols-3">
              {HERO_PRODUCTS.map((p) => (
                <SmartLink
                  key={p.name}
                  href={p.href}
                  className="group flex items-center gap-3.5 rounded-2xl border sm:last:odd:col-span-2 lg:last:odd:col-span-1 border-border bg-background px-4 py-3.5 text-left shadow-[0_1px_2px_hsl(0_0%_0%/0.04)] transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-0.5 hover:border-foreground/25 hover:shadow-[0_18px_40px_-24px_hsl(0_0%_0%/0.45)] active:translate-y-0"
                >
                  <img src={p.logo} alt="" width={38} height={38} className="h-[38px] w-[38px] shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[16px] font-semibold leading-tight tracking-[-0.01em] text-foreground">{p.name}</span>
                    <span className="mt-0.5 block text-[13.5px] leading-snug text-muted-foreground">{p.line}</span>
                  </span>
                  <ArrowRight className="h-4.5 w-4.5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-foreground" />
                </SmartLink>
              ))}
            </div>
            <div className="mt-6 flex items-center justify-center gap-8">
              <a href="#products" className="text-[16px] font-medium text-foreground/70 underline-offset-4 hover:text-foreground hover:underline">
                Compare our products
              </a>
              <SmartLink href="/request-demo" className="group inline-flex items-center gap-1.5 text-[16px] font-semibold text-[hsl(216_84%_45%)]">
                Contact us <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </SmartLink>
            </div>
          </Reveal>
          <Reveal delay={0.12} y={36} className="mt-14 sm:mt-16">
            <HeroBoard />
          </Reveal>
        </div>
        <AppDock />
      </section>

      {/* Areas of work */}
      <section aria-labelledby="areas-h" className="py-24 sm:py-32">
        <div className="cm-container">
          <Reveal>
            <h2 id="areas-h" className="mx-auto max-w-4xl text-center text-[2.6rem] font-medium leading-[1.06] tracking-[-0.035em] sm:text-[4rem]">Transform how work gets done</h2>
            <p className="mx-auto mt-5 max-w-3xl text-center text-[1.2rem] leading-snug text-foreground/85 sm:text-[1.45rem]">
              Four kinds of work quietly eat the day. We build AI products for each of them, all with one login.
            </p>
          </Reveal>
          <Reveal delay={0.06} className="mt-12">
            <WorkAreas />
          </Reveal>
        </div>
      </section>

      {/* What ClearMakes is */}
      <section aria-labelledby="what-h" className="border-t border-border py-24 sm:py-36">
        <div className="cm-container grid gap-12 lg:grid-cols-[1fr_1.2fr] lg:gap-24">
          <Reveal>
            <p className="cm-eyebrow mb-4">What we are building</p>
            <h2 id="what-h" className="text-[2.2rem] font-semibold leading-[1.06] tracking-[-0.035em] sm:text-[3.2rem]">A family of AI products, each built around one real problem.</h2>
          </Reveal>
          <Reveal delay={0.08} className="space-y-6 lg:pt-10">
            <p className="text-[1.35rem] leading-[1.45] tracking-[-0.01em] text-foreground sm:text-[1.6rem]">
              Modern work is full of effort that produces nothing: copying a message into another app,
              re-answering the same question, waiting for the approval.
            </p>
            <p className="cm-lead">
              ClearMakes exists to remove it. We pick a recurring problem, build the smallest product that makes it
              disappear, and let AI agents do the carrying while people keep the decisions. Every product shares one
              workspace and one login, so the family grows without getting heavier.
            </p>
            <p className="cm-lead">
              Three are live today.{" "}
              <SmartLink href="/products/inbox" className="font-medium text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground">ClearMakes Inbox</SmartLink>{" "}
              connects Gmail, Outlook, Slack, Teams and more, runs workflows on new messages, and answers everyday questions with Smart FAQ.{" "}
              <SmartLink href="https://agents.clearmakes.com" className="font-medium text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground">ClearMakes Agents</SmartLink>{" "}
              builds AI agents from your documents and apps, for your team and on your website.{" "}
              <SmartLink href="https://meet.clearmakes.com" className="font-medium text-foreground underline decoration-border underline-offset-4 hover:decoration-foreground">ClearMeet</SmartLink>{" "}
              answers questions during your calls and writes the notes.
            </p>
            <SmartLink href="/company" className="group inline-flex items-center gap-1.5 text-[16px] font-medium text-[hsl(216_84%_45%)]">
              Read about the company <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </SmartLink>
          </Reveal>
        </div>
      </section>

      {/* Scroll-driven story */}
      <ScrollStory />

      {/* Products */}
      <section id="products" aria-labelledby="products-h" className="scroll-mt-20 border-t border-border bg-[hsl(40_9%_97%)] py-24 sm:py-36">
        <div className="cm-container">
          <Reveal>
            <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="cm-eyebrow mb-4">Products</p>
                <h2 id="products-h" className="max-w-2xl text-[2.2rem] font-semibold leading-[1.06] tracking-[-0.035em] sm:text-[3.2rem]">The ClearMakes family.</h2>
                <p className="cm-lead mt-5 max-w-xl">Each product stands on its own, has its own page, and opens from the same workspace.</p>
              </div>
              <SmartLink href="/products" className="group inline-flex items-center gap-1.5 text-[15px] font-medium text-foreground">
                All products <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </SmartLink>
            </div>
          </Reveal>
          {/* one product reads as a feature; two or more fall into a grid on their own */}
          <ul className={`mt-14 grid gap-5 ${liveProducts.length > 1 ? "sm:grid-cols-2 lg:grid-cols-3" : ""}`}>
            {liveProducts.map((p, i) => {
              const solo = liveProducts.length === 1;
              return (
                <Reveal key={p.slug} delay={i * 0.05}>
                  <li className="group/card relative h-full list-none">
                    <SmartLink
                      href={productPath(p)}
                      className={`group flex h-full rounded-3xl border border-border bg-background p-8 transition-[transform,box-shadow] duration-300 group-hover/card:-translate-y-1 group-hover/card:shadow-[0_30px_60px_-30px_hsl(0_0%_0%/0.25)] sm:p-10 ${
                        solo ? "flex-col gap-10 lg:flex-row lg:items-end lg:justify-between" : "min-h-[300px] flex-col"
                      }`}
                    >
                      <div className={solo ? "max-w-2xl" : "flex flex-1 flex-col"}>
                        <img src={p.logo} alt="" width={48} height={48} className="h-12 w-12 transition-transform duration-300 group-hover:-rotate-6" />
                        <h3 className={`font-semibold tracking-[-0.03em] ${solo ? "mt-10 text-[2rem] sm:text-[2.6rem]" : "mt-auto pt-12 text-[1.6rem]"}`}>{p.name}</h3>
                        <p className={solo ? "cm-lead mt-3" : "cm-body mt-2"}>{solo ? p.description : p.tagline}</p>
                        {/* what each product does, in a few words: all of them alone, three in the grid */}
                        <div className={`flex flex-wrap gap-2 ${solo ? "mt-7" : "mb-7 mt-5"}`}>
                          {p.highlights.slice(0, solo ? 5 : 3).map((h) => (
                            <span key={h} className="rounded-full border border-border px-3 py-1 text-[13px] text-foreground">{h}</span>
                          ))}
                        </div>
                      </div>
                      <span className="inline-flex shrink-0 items-center gap-3 text-[16px] font-medium">
                        Learn more
                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-foreground text-background transition-transform duration-200 group-hover:translate-x-1">
                          <ArrowRight className="h-4 w-4" />
                        </span>
                      </span>
                    </SmartLink>
                    {/* a desktop app also gets Download, a separate link sitting on the card's corner */}
                    {p.download && (
                      <a
                        href={p.download.href}
                        className="absolute right-8 top-8 z-10 inline-flex h-10 items-center gap-2 rounded-full border border-border bg-background px-4 text-[15px] font-medium text-foreground transition-[transform,background-color,color] duration-300 hover:bg-foreground hover:text-background group-hover/card:-translate-y-1 sm:right-10 sm:top-10"
                      >
                        <Download className="h-4 w-4" /> {p.download.label}
                      </a>
                    )}
                  </li>
                </Reveal>
              );
            })}
          </ul>
        </div>
      </section>

      {/* Beliefs */}
      <section aria-labelledby="beliefs-h" className="py-24 sm:py-36">
        <div className="cm-container">
          <Reveal>
            <p className="cm-eyebrow mb-4">How we build</p>
            <h2 id="beliefs-h" className="max-w-3xl text-[2.2rem] font-semibold leading-[1.06] tracking-[-0.035em] sm:text-[3.2rem]">Four rules every ClearMakes product follows.</h2>
          </Reveal>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {beliefs.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.05} className="group rounded-3xl bg-[hsl(40_9%_96%)] p-8 transition-colors duration-300 hover:bg-foreground hover:text-background">
                <b.icon className="h-6 w-6" strokeWidth={1.75} />
                <h3 className="mt-10 text-[19px] font-semibold tracking-[-0.015em]">{b.title}</h3>
                <p className="mt-2.5 text-[15px] leading-relaxed text-muted-foreground transition-colors duration-300 group-hover:text-background/70">{b.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="mt-24">
          <Marquee speed={46}>
            {facts.map((f) => (
              <span key={f} className="flex shrink-0 items-center gap-12 text-[1.6rem] font-semibold tracking-[-0.025em] text-foreground sm:text-[2rem]">
                {f} <span className="h-2 w-2 rounded-full bg-[hsl(216_84%_52%)]" />
              </span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* Resources */}
      <section aria-labelledby="learn-h" className="border-t border-border py-24 sm:py-36">
        <div className="cm-container">
          <Reveal>
            <h2 id="learn-h" className="max-w-2xl text-[2.2rem] font-semibold leading-[1.06] tracking-[-0.035em] sm:text-[3.2rem]">Get to know ClearMakes.</h2>
          </Reveal>
          <Reveal delay={0.06} className="mt-12">
            <ResourceRail cards={resources} />
          </Reveal>
        </div>
      </section>

      {/* Closing */}
      <section aria-labelledby="cta-h" className="bg-[hsl(0_0%_7%)] text-white">
        <div className="cm-container py-28 text-center sm:py-40">
          <Reveal>
            <h2 id="cta-h" className="mx-auto max-w-4xl text-[2.6rem] font-semibold leading-[1.02] tracking-[-0.04em] sm:text-[4.4rem]">Spend your day on the work that needs you.</h2>
            <p className="mx-auto mt-7 max-w-xl text-[19px] leading-relaxed text-white/70">One free ClearMakes workspace opens every product we make.</p>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <SmartLink href={GET_STARTED_URL} className="inline-flex h-12 w-full items-center justify-center rounded-lg bg-white px-6 text-[16px] font-medium text-black transition-colors hover:bg-white/85 sm:w-auto">Get started free</SmartLink>
              <SmartLink href="/products" className="inline-flex h-12 w-full items-center justify-center rounded-lg border border-white/30 px-6 text-[16px] font-medium text-white transition-colors hover:bg-white/10 sm:w-auto">Explore products</SmartLink>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
    <Footer />
  </>
);

export default Index;
